import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { listItems, type Item, type Sort, type Tab } from "@/api/items";
import { FavoriteCard } from "@/components/FavoriteCard";
import { ApiError } from "@/api/client";
import { useAppStore } from "@/store/auth";

const TABS: { value: Tab; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "own", label: "我的" },
  { value: "shared", label: "共享给我的" },
];

const SORTS: { value: Sort; label: string }[] = [
  { value: "list_time", label: "收藏时间" },
  { value: "original_time", label: "原消息时间" },
];

const HIGHLIGHT_MS = 1500;

export default function FavoritesPage() {
  const [params, setParams] = useSearchParams();
  const focusShareIdParam = params.get("share");
  const focusShareId = focusShareIdParam ? Number(focusShareIdParam) : null;

  // 深链来时强制 tab=shared
  const initialTab: Tab = focusShareId ? "shared" : "all";
  const [tab, setTab] = useState<Tab>(initialTab);
  const [sort, setSort] = useState<Sort>("list_time");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [items, setItems] = useState<Item[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // share_id → DOM 节点 ref，用于深链滚动 + 高亮
  const cardRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  const focusConsumedRef = useRef(false);

  // debounce 搜索框
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const reqSeqRef = useRef(0);
  const reload = useCallback(() => {
    const seq = ++reqSeqRef.current;
    setLoading(true);
    setError(null);
    setItems([]);
    setCursor(null);

    // 仅"首次加载"时把 focus_share_id 传给后端，后续筛选/分页不带，
    // 否则用户改 tab/搜索时还会被钉在那一页上。
    const useFocus = focusShareId && !focusConsumedRef.current;

    listItems({
      tab,
      sort,
      q: debouncedQuery,
      focus_share_id: useFocus ? focusShareId : null,
    })
      .then((res) => {
        if (seq !== reqSeqRef.current) return;
        setItems(res.items);
        setCursor(res.next_cursor);
      })
      .catch((e: unknown) => {
        if (seq !== reqSeqRef.current) return;
        setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (seq === reqSeqRef.current) setLoading(false);
      });
  }, [tab, sort, debouncedQuery, focusShareId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // 收藏成功后自动刷新列表
  const favoritesVersion = useAppStore((s) => s.favoritesVersion);
  const prevVersionRef = useRef(favoritesVersion);
  useEffect(() => {
    if (favoritesVersion !== prevVersionRef.current) {
      prevVersionRef.current = favoritesVersion;
      reload();
    }
  }, [favoritesVersion, reload]);

  // 深链消费：列表挂载完成后，找到 share_id 对应的 DOM 滚动 + 高亮
  useEffect(() => {
    if (focusConsumedRef.current || !focusShareId || items.length === 0) return;
    const key = `shared:${focusShareId}`;
    const el = cardRefs.current.get(key);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightKey(key);
    focusConsumedRef.current = true;

    const t = setTimeout(() => {
      setHighlightKey(null);
      // 清掉 URL 上的 ?share=…，避免分享或刷新时重复触发滚动
      const next = new URLSearchParams(params);
      next.delete("share");
      setParams(next, { replace: true });
    }, HIGHLIGHT_MS);
    return () => clearTimeout(t);
  }, [focusShareId, items, params, setParams]);

  const loadMore = () => {
    if (!cursor || loading) return;
    const seq = reqSeqRef.current;
    setLoading(true);
    listItems({ tab, sort, q: debouncedQuery, cursor })
      .then((res) => {
        if (seq !== reqSeqRef.current) return;
        setItems((prev) => [...prev, ...res.items]);
        setCursor(res.next_cursor);
      })
      .catch((e: unknown) => {
        if (seq !== reqSeqRef.current) return;
        setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (seq === reqSeqRef.current) setLoading(false);
      });
  };

  const itemKey = (it: Item) =>
    `${it.kind}:${it.kind === "shared" ? it.share_id : it.favorite_id}`;

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col gap-4 p-4">
      <div className="sticky top-12 z-10 flex flex-col gap-2 bg-slate-50/90 pb-2 pt-1 backdrop-blur">
        <div className="relative flex items-center">
          <SearchIcon />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="搜索收藏内容…"
            className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm outline-none focus:border-sky-400"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full border border-slate-200 bg-white p-0.5 text-xs">
            {TABS.map((t) => (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={`rounded-full px-3 py-1 transition ${
                  tab === t.value
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <label className="ml-auto flex items-center gap-2 text-xs text-slate-600">
            <span>排序</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as Sort)}
              className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs outline-none focus:border-sky-400"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {error ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            加载失败：{error}
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <Skeleton />
        ) : items.length === 0 && !loading ? (
          <EmptyState hasQuery={!!debouncedQuery} />
        ) : (
          <>
            {items.map((it) => {
              const key = itemKey(it);
              return (
                <FavoriteCard
                  key={key}
                  item={it}
                  highlight={highlightKey === key}
                  innerRef={(el) => {
                    if (el) cardRefs.current.set(key, el);
                    else cardRefs.current.delete(key);
                  }}
                  onDeleted={() =>
                    setItems((prev) =>
                      prev.filter(
                        (x) =>
                          !(x.kind === "own" && x.favorite_id === it.favorite_id),
                      ),
                    )
                  }
                  onPinChanged={reload}
                  onTitleChanged={reload}
                />
              );
            })}

            {cursor ? (
              <button
                onClick={loadMore}
                disabled={loading}
                className="mx-auto mt-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs text-slate-600 disabled:opacity-50"
              >
                {loading ? "加载中…" : "加载更多"}
              </button>
            ) : items.length > 0 ? (
              <div className="py-4 text-center text-xs text-slate-400">— 没有更多了 —</div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-28 animate-pulse rounded-xl border border-slate-200 bg-white"
        />
      ))}
    </div>
  );
}

function EmptyState({ hasQuery }: { hasQuery: boolean }) {
  if (hasQuery) {
    return (
      <div className="flex flex-col items-center gap-1 py-16 text-sm text-slate-500">
        <div className="text-4xl">🔎</div>
        <div>没有匹配的收藏</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2 py-16 text-sm text-slate-500">
      <div className="text-4xl">📌</div>
      <div>还没有收藏</div>
      <div className="text-xs text-slate-400">
        在飞书里长按一条消息，点击"收藏到我的知识库"即可
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      aria-hidden
      className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M9 3.5a5.5 5.5 0 1 0 3.354 9.854l3.146 3.146a.75.75 0 1 0 1.06-1.06l-3.146-3.146A5.5 5.5 0 0 0 9 3.5ZM5 9a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
